import React, { useState, useEffect } from 'react';
import {
  Search, Users, Hash, Video, Calendar, Radio, Plus, X,
  Copy, PlayCircle, Sparkles, Check, ExternalLink, ShieldCheck,
  MessageSquare, Send, CheckCircle2, ArrowRight
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';

/* ──────────────────────────────────────────
   GROUP DISCUSSION / CHAT MODAL
   ────────────────────────────────────────── */
function GroupDiscussionModal({ community, onClose, user }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      author: 'Aarav Patel',
      avatar: 'Aarav',
      time: '10 mins ago',
      text: `Welcome everyone to ${community.name}! What topics are you revising today?`,
    },
    {
      id: 2,
      author: 'Sneha Roy',
      avatar: 'Sneha',
      time: '4 mins ago',
      text: 'Just finished the quiz on this topic in the consistency engine. Highly recommend checking the notes in Digital Library!',
    },
  ]);
  const [inputMsg, setInputMsg] = useState('');

  const groupMeetLink = `https://meet.google.com/new`;

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputMsg.trim()) return;
    const newMsg = {
      id: Date.now(),
      author: user?.full_name || user?.name || 'You',
      avatar: user?.name || 'CurrentUser',
      time: 'Just now',
      text: inputMsg.trim(),
    };
    setMessages((prev) => [...prev, newMsg]);
    setInputMsg('');
  };

  const icons = { users: Users, hash: Hash };
  const Icon = icons[community.icon] || Users;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(6px)',
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '640px',
          width: '94vw',
          height: '82vh',
          maxHeight: '700px',
          borderRadius: '1.75rem',
          background: '#ffffff',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'scaleIn 0.2s cubic-bezier(0.25, 1, 0.5, 1) both',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.75rem',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderBottom: '1px solid rgba(217, 119, 6, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div
              style={{
                width: 44,
                height: 44,
                background: '#ffffff',
                borderRadius: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <Icon size={22} color="#d97706" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
                  {community.name}
                </h3>
                <span
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    color: '#059669',
                    background: 'rgba(16,185,129,0.15)',
                    padding: '2px 7px',
                    borderRadius: 12,
                  }}
                >
                  Active Group
                </span>
              </div>
              <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#78350f', fontWeight: 600 }}>
                {community.members || 0} Members · Peer Discussion & Doubts
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Live Group Video Meeting Banner */}
        <div
          style={{
            padding: '0.75rem 1.5rem',
            background: '#ecfdf5',
            borderBottom: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#065f46', fontWeight: 700 }}>
            <Video size={15} color="#059669" />
            <span>Live study session in progress? Hop on Google Meet with peers:</span>
          </div>
          <a
            href={groupMeetLink}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '0.45rem 1rem',
              borderRadius: 20,
              background: '#059669',
              color: '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 800,
              textDecoration: 'none',
              boxShadow: '0 2px 6px rgba(5,150,105,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            <Video size={13} /> Open Google Meet <ExternalLink size={11} />
          </a>
        </div>

        {/* Message Thread */}
        <div
          style={{
            flex: 1,
            padding: '1.25rem 1.5rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            background: '#f8fafc',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              padding: '0.5rem 1rem',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: 20,
              fontSize: '0.72rem',
              color: '#64748b',
              fontWeight: 600,
              margin: '0 auto',
            }}
          >
            💬 Study chat for {community.name}. Ask questions or share study resources!
          </div>

          {messages.map((m) => (
            <div key={m.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <img
                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${m.avatar}`}
                alt={m.author}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '1.5px solid #cbd5e1',
                  background: '#fff',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{m.author}</span>
                  <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{m.time}</span>
                </div>
                <div
                  style={{
                    background: '#ffffff',
                    padding: '0.75rem 1rem',
                    borderRadius: '0 1rem 1rem 1rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.83rem',
                    color: '#334155',
                    lineHeight: 1.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  }}
                >
                  {m.text}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <form
          onSubmit={handleSend}
          style={{
            padding: '1rem 1.25rem',
            background: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            value={inputMsg}
            onChange={(e) => setInputMsg(e.target.value)}
            placeholder={`Ask a question or share a study tip in ${community.name}...`}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              border: '1.5px solid #e2e8f0',
              fontSize: '0.85rem',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            type="submit"
            disabled={!inputMsg.trim()}
            style={{
              padding: '0.75rem 1.25rem',
              background: inputMsg.trim() ? 'var(--primary-gold)' : '#e2e8f0',
              color: inputMsg.trim() ? '#ffffff' : '#94a3b8',
              border: 'none',
              borderRadius: '0.75rem',
              fontWeight: 800,
              fontSize: '0.85rem',
              cursor: inputMsg.trim() ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              boxShadow: inputMsg.trim() ? '0 2px 8px var(--gold-shadow)' : 'none',
            }}
          >
            <Send size={14} /> Send
          </button>
        </form>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   JOIN PEER ROOM CONFIRMATION MODAL
   ────────────────────────────────────────── */
function JoinPeerRoomConfirmationModal({ session, onConfirm, onClose, loading }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(6px)',
        }}
        onClick={!loading ? onClose : undefined}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '520px',
          width: '92vw',
          borderRadius: '1.75rem',
          background: '#ffffff',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          animation: 'scaleIn 0.2s cubic-bezier(0.25, 1, 0.5, 1) both',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        {/* Top Header */}
        <div
          style={{
            padding: '1.5rem 1.75rem',
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            borderBottom: '1px solid rgba(16, 185, 129, 0.15)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 20,
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                color: '#059669',
                fontSize: '0.72rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6,
              }}
            >
              <Sparkles size={12} /> Google Meet Live Video Room
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
              Join Study Room
            </h3>
          </div>

          <button
            onClick={!loading ? onClose : undefined}
            disabled={loading}
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body Overview */}
        <div style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              padding: '1rem',
              background: '#f8fafc',
              borderRadius: '1.25rem',
              border: '1px solid #e2e8f0',
            }}
          >
            <img
              src={session.isDefaultRoom ? '/Logo.jpeg' : `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.host_name || session.id}`}
              alt={session.host_name}
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: '2px solid #10b981',
                background: '#fff',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#059669', textTransform: 'uppercase' }}>
                {session.subject || 'Collaborative Study'}
              </span>
              <h4 style={{ margin: '0.15rem 0', fontSize: '1rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
                {session.title}
              </h4>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                Platform: <strong style={{ color: '#059669' }}>Google Meet Video Call</strong>
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>
                <Users size={12} color="#10b981" /> Capacity
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 2 }}>
                {session.enrolled_count || 0} / {session.total_capacity || 30} Seats
              </div>
            </div>

            <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>
                <Video size={12} color="#059669" /> Video Link
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#059669', marginTop: 2 }}>
                Google Meet Ready
              </div>
            </div>
          </div>

          <div style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.15)', borderRadius: '1rem', padding: '0.85rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#065f46', fontSize: '0.78rem', fontWeight: 800, marginBottom: 4 }}>
              <ShieldCheck size={14} color="#059669" /> What happens when you confirm:
            </div>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#047857', lineHeight: 1.5 }}>
              You will instantly unlock the direct <strong>Google Meet link</strong> to join live audio/video and screen sharing with your peers.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1.25rem 1.75rem',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.65rem 1.25rem',
              background: '#ffffff',
              border: '1.5px solid #cbd5e1',
              borderRadius: '0.85rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#475569',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.65rem 1.5rem',
              background: 'linear-gradient(135deg,#10b981,#059669)',
              border: 'none',
              borderRadius: '0.85rem',
              fontSize: '0.88rem',
              fontWeight: 800,
              color: '#ffffff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
            }}
          >
            {loading ? (
              'Unlocking Google Meet...'
            ) : (
              <>
                <Video size={16} /> Enter Google Meet Room
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Peer session card ── */
function PeerSessionCard({ session, user, isEnrolled, enrolledLink, onJoinRequest, onStart }) {
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);

  const isFull = session.enrolled_count >= session.total_capacity;
  const isLive = session.status === 'live';
  const isHost = user && session.host_id && (session.host_id === user.id || session.host_id?.id === user.id);
  const quorumMet = session.enrolled_count >= (session.minAttendeesToStart ?? 10);

  const activeLink = enrolledLink || session.liveClassLink || session.meetingLink || 'https://meet.google.com/new';
  const userHasAccess = isEnrolled || session.isEnrolled || isHost || session.isDefaultRoom;

  const handleStart = async () => {
    setStarting(true);
    await onStart(session);
    setStarting(false);
  };

  const handleCopy = () => {
    if (!activeLink) return;
    navigator.clipboard?.writeText(activeLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="glass-card"
      style={{
        display: 'flex', flexDirection: 'column', gap: '1rem',
        borderRadius: '1.25rem', background: '#ffffff',
        border: userHasAccess ? '2px solid #10b981' : '1px solid rgba(0,0,0,0.06)',
        position: 'relative', overflow: 'hidden', transition: 'all 0.25s cubic-bezier(.22,1,.36,1)',
        boxShadow: userHasAccess ? '0 8px 24px rgba(16,185,129,0.08)' : '0 4px 16px rgba(0,0,0,0.02)',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg,#10b981,#34d399)' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <div style={{ position: 'relative' }}>
          <img
            src={session.isDefaultRoom ? '/Logo.jpeg' : `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.host_name || session.id}`}
            alt={session.host_name}
            style={{ width: '3.25rem', height: '3.25rem', borderRadius: '50%', border: '2px solid rgba(16,185,129,0.2)', background: 'rgba(16,185,129,0.05)', objectFit: 'cover' }}
          />
          {isLive && (
            <span style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff', boxShadow: '0 0 6px #22c55e' }} />
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '0.92rem', margin: 0 }}>{session.host_name || 'Study Circle'}</h4>
            {userHasAccess && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)', fontSize: '0.68rem', fontWeight: 800, padding: '2px 7px', borderRadius: 20 }}>
                <Check size={11} /> Joined
              </span>
            )}
          </div>
          <p style={{ color: '#059669', fontSize: '0.72rem', fontWeight: 700, margin: '2px 0 0' }}>
            {session.subject || 'General'}{session.topic ? ` · ${session.topic}` : ''}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
            {isLive && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Radio size={11} color="#22c55e" />
                <span style={{ color: '#22c55e', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Live Now</span>
              </span>
            )}
            {session.isDefaultRoom && (
              <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#2563eb', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 6, padding: '1px 6px', textTransform: 'uppercase' }}>Standing Room</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <h5 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '0.92rem', margin: '0 0 0.25rem' }}>{session.title}</h5>
        <p style={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{session.description || 'A student-hosted study room — join in and learn together.'}</p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {session.date && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.7rem', padding: '0.2rem 0.6rem', background: 'rgba(16,185,129,0.06)', color: '#059669', borderRadius: '0.5rem', border: '1px solid rgba(16,185,129,0.15)', fontWeight: 700 }}>
            <Calendar size={11} />{new Date(session.date).toLocaleDateString('en-IN')}
          </span>
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.7rem', padding: '0.2rem 0.6rem', background: !session.isDefaultRoom && !isLive ? (quorumMet ? 'rgba(16,185,129,0.08)' : 'rgba(217,119,6,0.08)') : 'rgba(0,0,0,0.03)', color: !session.isDefaultRoom && !isLive ? (quorumMet ? '#059669' : '#d97706') : '#64748b', borderRadius: '0.5rem', border: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          <Users size={11} />{session.enrolled_count || 0}/{session.total_capacity || 30}
          {!session.isDefaultRoom && !isLive && ` (needs ${session.minAttendeesToStart ?? 10} to start)`}
        </span>
      </div>

      {/* Host controls: quorum-gated "start class" */}
      {isHost && !isLive && (
        <button
          onClick={handleStart}
          disabled={!quorumMet || starting}
          title={!quorumMet ? `Waiting for ${(session.minAttendeesToStart ?? 10) - session.enrolled_count} more member(s) to join` : ''}
          style={{
            width: '100%', padding: '0.625rem', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.8rem',
            cursor: quorumMet ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            background: quorumMet ? 'linear-gradient(135deg,#10b981,#059669)' : 'rgba(0,0,0,0.04)',
            color: quorumMet ? '#fff' : '#94a3b8', border: 'none',
          }}
        >
          <PlayCircle size={14} />
          {starting ? 'Starting...' : quorumMet ? 'Start Live Room' : `Waiting for ${session.minAttendeesToStart ?? 10} members`}
        </button>
      )}

      {/* Live class link once joined/unlocked */}
      {userHasAccess ? (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <a
            href={activeLink}
            target="_blank"
            rel="noreferrer"
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
              padding: '0.65rem', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.82rem',
              background: '#059669', color: '#ffffff', textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(5,150,105,0.25)',
            }}
          >
            <Video size={14} /> Open Google Meet <ExternalLink size={12} />
          </a>
          <button
            onClick={handleCopy}
            title="Copy meeting link"
            style={{
              padding: '0.65rem 0.85rem', borderRadius: '0.75rem',
              background: '#f8fafc', border: '1.5px solid #e2e8f0',
              color: copied ? '#10b981' : '#64748b', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
          </button>
        </div>
      ) : (
        <button
          onClick={() => onJoinRequest(session)}
          disabled={isFull}
          style={{
            width: '100%', padding: '0.65rem', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.82rem',
            cursor: isFull ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            background: isFull ? 'rgba(0,0,0,0.04)' : 'linear-gradient(135deg,#10b981,#059669)',
            color: isFull ? '#94a3b8' : '#ffffff',
            border: 'none',
            boxShadow: isFull ? 'none' : '0 2px 8px rgba(16,185,129,0.25)',
          }}
        >
          <Video size={14} />
          {isFull ? 'Room Full' : 'Join Google Meet Room'}
        </button>
      )}
    </div>
  );
}

/* ── Host-a-session modal ── */
function HostSessionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', subject: '', topic: '', description: '', date: '', time: '', duration: '30 min', total_capacity: 30, meetingLink: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inputStyle = { width: '100%', padding: '0.75rem 0.9rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.85rem', color: '#0f172a' };
  const labelStyle = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', marginBottom: '0.35rem' };
  const MEETING_LINK_PATTERN = /^https:\/\/(meet\.google\.com\/|teams\.microsoft\.com\/|teams\.live\.com\/)/i;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!MEETING_LINK_PATTERN.test(form.meetingLink)) {
      setError('Please paste a valid Google Meet or Microsoft Teams link');
      return;
    }
    setSaving(true);
    try {
      await api.createPeerSession(form);
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not create the room');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflowY: 'auto' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.4)' }} onClick={onClose} />
      <div className="glass-card" style={{ position: 'relative', width: '92%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)' }}>Host a Live Room</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 700, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '0.75rem', padding: '0.7rem', marginBottom: '1rem' }}>{error}</div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          <div><label style={labelStyle}>Title</label><input style={inputStyle} required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Let's crack Dynamic Programming" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div><label style={labelStyle}>Subject</label><input style={inputStyle} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="DSA" /></div>
            <div><label style={labelStyle}>Topic</label><input style={inputStyle} value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder="dynamic_programming" /></div>
          </div>
          <div><label style={labelStyle}>Description</label><textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><label style={labelStyle}>Time</label><input type="time" style={inputStyle} value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></div>
          </div>
          <div><label style={labelStyle}>Room Size</label><input type="number" max={30} style={inputStyle} value={form.total_capacity} onChange={e => setForm({ ...form, total_capacity: e.target.value })} /></div>
          <div>
            <label style={labelStyle}>Meeting Link (Google Meet or Microsoft Teams)</label>
            <input style={inputStyle} required value={form.meetingLink} onChange={e => setForm({ ...form, meetingLink: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij" />
          </div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.6rem', padding: '0.6rem 0.8rem' }}>
            Rooms hold up to 30 members. Once at least 10 have joined, you'll be able to start the class.
          </div>
          <button type="submit" disabled={saving} style={{ marginTop: '0.25rem', padding: '0.8rem', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.85rem', cursor: saving ? 'not-allowed' : 'pointer', border: 'none', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating...' : 'Open the Room'}
          </button>
        </form>
      </div>
    </div>
  );
}

function CommunityCard({ c, isMember, onJoin, onOpenDiscussion }) {
  const icons = { users: Users, hash: Hash };
  const Icon = icons[c.icon] || Users;
  return (
    <div
      className="glass-card"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative', overflow: 'hidden', transition: 'all 0.3s',
        border: isMember ? '1.5px solid rgba(217, 119, 6, 0.3)' : '1px solid rgba(0,0,0,0.05)',
        background: isMember ? 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)' : '#ffffff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: 1 }}>
        <div style={{ width: '3.25rem', height: '3.25rem', background: 'rgba(244,196,48,0.12)', borderRadius: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(244,196,48,0.25)', flexShrink: 0 }}>
          <Icon size={22} color="#d4a017" />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '0.92rem', margin: 0 }}>{c.name}</h4>
            {isMember && (
              <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#b45309', background: 'rgba(244,196,48,0.18)', padding: '1px 6px', borderRadius: 10 }}>
                Joined
              </span>
            )}
          </div>
          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {c.description}
          </p>
          <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700 }}>{c.members || 0} Members</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '0.75rem' }}>
        {isMember ? (
          <button
            onClick={() => onOpenDiscussion(c)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '0.6rem 1.15rem',
              background: 'linear-gradient(135deg,#f4c430,#d4a017)', color: 'black',
              fontSize: '0.78rem', fontWeight: 800, borderRadius: '0.75rem', cursor: 'pointer',
              border: 'none', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(244,196,48,0.25)',
            }}
          >
            <MessageSquare size={13} /> Chat & Meet
          </button>
        ) : (
          <button
            onClick={() => onJoin(c)}
            style={{
              padding: '0.6rem 1.25rem', background: '#f8fafc', color: 'var(--navy-dark)',
              fontSize: '0.78rem', fontWeight: 800, borderRadius: '0.75rem', cursor: 'pointer',
              border: '1.5px solid #e2e8f0', fontFamily: 'inherit',
            }}
          >
            Join Group
          </button>
        )}
      </div>
    </div>
  );
}

export default function Community() {
  const [sessions, setSessions] = useState([]);
  const [joinedSessionMap, setJoinedSessionMap] = useState(new Map());
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [communities, setCommunities] = useState([]);
  const [joinedCommunityIds, setJoinedCommunityIds] = useState(new Set());
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [showHostModal, setShowHostModal] = useState(false);
  const [confirmingSession, setConfirmingSession] = useState(null);
  const [joiningLoading, setJoiningLoading] = useState(false);
  const [activeDiscussionCommunity, setActiveDiscussionCommunity] = useState(null);

  const { user } = useAuth();
  const { showAlert } = useAlert();

  const loadSessions = () => {
    setSessionsLoading(true);
    api.getCollection('peer_sessions').then(data => {
      setSessions(data || []);
      setSessionsLoading(false);
    }).catch(() => setSessionsLoading(false));
  };

  const loadCommunities = () => {
    setLoading(true);
    api.getCollection('communities').then(data => {
      setCommunities(data || []);
      setFiltered(data || []);
      const memberSet = new Set((data || []).filter(c => c.isMember).map(c => c.id || c._id));
      setJoinedCommunityIds(memberSet);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadSessions();
    loadCommunities();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q ? communities.filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)) : communities);
  }, [search, communities]);

  const handleJoinRequest = (session) => {
    if (!user) { showAlert('Login Required', 'Please login to join a live room.'); return; }
    setConfirmingSession(session);
  };

  const handleConfirmJoin = async () => {
    if (!confirmingSession) return;
    setJoiningLoading(true);
    const sId = confirmingSession.id || confirmingSession._id;
    try {
      const res = await api.joinPeerSession(sId);
      const link = res?.liveClassLink || confirmingSession.meetingLink || 'https://meet.google.com/new';
      
      setJoinedSessionMap(prev => new Map(prev.set(sId, link)));
      if (res?.alreadyJoined) {
        showAlert('Google Meet Ready! 🚀', `You're in! Click "Open Google Meet" to join live.`);
      } else {
        showAlert('Room Joined! 🚀', `Your Google Meet link is unlocked below.`);
      }
      setConfirmingSession(null);
      loadSessions();
    } catch (e) {
      const message = e?.message?.includes('full') ? 'This room just reached capacity.'
        : e?.message?.includes('Already joined') ? 'You already joined this room.'
        : 'Could not join this room. Please try again.';
      showAlert('Join Status', message);
      if (e?.message?.includes('Already joined')) {
        const link = confirmingSession.meetingLink || 'https://meet.google.com/new';
        setJoinedSessionMap(prev => new Map(prev.set(sId, link)));
      }
      setConfirmingSession(null);
    } finally {
      setJoiningLoading(false);
    }
  };

  const handleStartSession = async (session) => {
    try {
      const { liveClassLink } = await api.startPeerSession(session.id || session._id);
      showAlert('Class Started!', 'Your room is now live — share the Google Meet link with everyone who joined.');
      const sId = session.id || session._id;
      setJoinedSessionMap(prev => new Map(prev.set(sId, liveClassLink)));
      setTimeout(loadSessions, 800);
      return liveClassLink;
    } catch (e) {
      showAlert('Not Enough Members Yet', e?.message || 'You need more members to join before starting.');
      return null;
    }
  };

  const handleJoinGroup = async (c) => {
    if (!user) { showAlert('Login Required', 'Please login to join communities.'); return; }
    const cId = c.id || c._id;
    try {
      const updated = await api.joinCommunity(cId);
      setCommunities(prev => prev.map(x => (x.id === cId || x._id === cId) ? { ...x, isMember: true, members: (x.members || 0) + 1 } : x));
      setJoinedCommunityIds(prev => new Set([...prev, cId]));
      if (updated?.alreadyMember) {
        showAlert('Welcome Back! 🚀', `Opening ${c.name} discussion & Google Meet.`);
      } else {
        showAlert('Joined! 🚀', `You have joined ${c.name}. Welcome!`);
      }
      setActiveDiscussionCommunity(c);
    } catch (e) {
      if (e?.message?.includes('Already a member')) {
        setJoinedCommunityIds(prev => new Set([...prev, cId]));
        showAlert('Already Joined', `Opening ${c.name} discussion & Google Meet.`);
        setActiveDiscussionCommunity(c);
      } else {
        showAlert('Error', 'Could not join this community. Please try again.');
      }
    }
  };

  // Filter joined sessions & joined communities
  const myJoinedSessions = sessions.filter(s => {
    const sId = s.id || s._id;
    return s.isEnrolled || joinedSessionMap.has(sId) || (user && s.host_id && s.host_id.toString() === user.id) || s.isDefaultRoom;
  });

  const myJoinedCommunities = communities.filter(c => {
    const cId = c.id || c._id;
    return c.isMember || joinedCommunityIds.has(cId);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      
      {/* Header Banner (3D Illustrated Backdrop with Social Proof & Stats) */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(6, 78, 59, 0.88) 0%, rgba(15, 23, 42, 0.85) 55%, rgba(6, 44, 34, 0.92) 100%), url("/peer_study_hero.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center 40%',
        borderRadius: '1.75rem', padding: '2.4rem 2.6rem',
        position: 'relative', overflow: 'hidden',
        border: '1px solid rgba(52, 211, 153, 0.3)',
        boxShadow: '0 20px 45px -10px rgba(6, 78, 59, 0.4)',
        color: '#ffffff',
      }}>
        {/* Ambient Glow Effects */}
        <div style={{ position: 'absolute', right: -40, top: -40, width: 280, height: 280, background: 'radial-gradient(circle, rgba(52, 211, 153, 0.25), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', left: '20%', bottom: -60, width: 240, height: 240, background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Top Row: Tag & CTA */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 800, color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '.1em' }}>
              <Sparkles size={13} color="#6ee7b7" /> Real-Time Collaboration Hub
            </div>

            <button
              onClick={() => { if (!user) { showAlert('Login Required', 'Please login to host a room.'); return; } setShowHostModal(true); }}
              className="btn-lift"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.6rem',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#ffffff',
                fontSize: '0.9rem', fontWeight: 800, borderRadius: '0.85rem', cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'inherit',
                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)',
                transition: 'all 0.2s cubic-bezier(.22,1,.36,1)',
              }}
            >
              <Plus size={18} /> Host a Study Room
            </button>
          </div>

          {/* Title & Description */}
          <div>
            <h2 style={{ fontSize: '2.35rem', fontWeight: 900, color: '#ffffff', margin: '0 0 0.5rem', letterSpacing: '-0.03em', lineHeight: 1.15 }}>
              Peer Connect & Study Circles
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#cbd5e1', margin: 0, maxWidth: '720px', lineHeight: 1.6 }}>
              Study collaboratively with peers in real time: hop on live <strong>Google Meet video calls</strong> or engage in <strong>subject channels</strong> to master complex concepts.
            </p>
          </div>

          {/* Social Proof & Live Metrics Bar */}
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
            paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.12)'
          }}>
            {/* Live Rooms Indicator */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(52, 211, 153, 0.35)', padding: '5px 12px', borderRadius: 20 }}>
              <span style={{ position: 'relative', display: 'flex', width: 8, height: 8 }}>
                <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#34d399', opacity: 0.75, animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
                <span style={{ position: 'relative', display: 'inline-flex', borderRadius: '50%', width: 8, height: 8, background: '#10b981' }} />
              </span>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#a7f3d0' }}>
                3 Video Rooms Live Now
              </span>
            </div>

            {/* Avatar Stack */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.12)', padding: '4px 10px 4px 6px', borderRadius: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {['Aarav', 'Sneha', 'Rohan', 'Priya'].map((seed, idx) => (
                  <img
                    key={seed}
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`}
                    alt={seed}
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      border: '1.5px solid #064e3b',
                      marginLeft: idx > 0 ? -6 : 0,
                      background: '#fff',
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#f1f5f9' }}>
                42+ Peers Studying Right Now
              </span>
            </div>

            {/* Feynman Study Multiplier Badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.35)', padding: '5px 12px', borderRadius: 20 }}>
              <Sparkles size={13} color="#fbbf24" />
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fef3c7' }}>
                Feynman Effect: +1.5x Study Boost
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* ── MY ACTIVE ENROLLED ROOMS & GROUPS SECTION ── */}
      {(myJoinedSessions.length > 0 || myJoinedCommunities.length > 0) && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '1.5rem', padding: '1.5rem 1.75rem',
          border: '1.5px solid rgba(16, 185, 129, 0.25)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.03)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
                My Joined Study Rooms & Active Circles
              </h3>
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#059669', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20 }}>
              {myJoinedSessions.length} Room(s) · {myJoinedCommunities.length} Group(s)
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {myJoinedSessions.map(s => {
              const sId = s.id || s._id;
              const link = joinedSessionMap.get(sId) || s.liveClassLink || s.meetingLink || 'https://meet.google.com/new';
              return (
                <div key={sId} style={{ background: '#ffffff', padding: '1.15rem', borderRadius: '1rem', border: '1.5px solid #10b981', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 4px 12px rgba(16,185,129,0.08)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#059669', textTransform: 'uppercase' }}>{s.subject || 'Live Room'}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(16,185,129,0.12)', color: '#059669', fontSize: '0.68rem', fontWeight: 800, padding: '2px 7px', borderRadius: 12 }}>
                      <CheckCircle2 size={11} /> Google Meet Ready
                    </span>
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{s.title}</h4>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>Hosted by {s.host_name || 'Ekalavya Scholar'}</p>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                        padding: '0.65rem', borderRadius: '0.75rem', background: '#059669', color: '#ffffff',
                        fontWeight: 800, fontSize: '0.82rem', textDecoration: 'none', boxShadow: '0 2px 6px rgba(5,150,105,0.25)'
                      }}
                    >
                      <Video size={14} /> Open Google Meet <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              );
            })}

            {myJoinedCommunities.map(c => {
              const cId = c.id || c._id;
              return (
                <div key={cId} style={{ background: '#ffffff', padding: '1.15rem', borderRadius: '1rem', border: '1.5px solid #d4a017', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 4px 12px rgba(212,160,23,0.08)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#b45309', textTransform: 'uppercase' }}>Study Group</span>
                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#b45309', background: 'rgba(244,196,48,0.2)', padding: '2px 7px', borderRadius: 12 }}>
                      Member
                    </span>
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{c.name}</h4>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>{c.members || 0} active student peers</p>
                  
                  <button
                    onClick={() => setActiveDiscussionCommunity(c)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      padding: '0.65rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#f4c430,#d4a017)',
                      color: 'black', fontWeight: 800, fontSize: '0.82rem', border: 'none', cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(244,196,48,0.2)'
                    }}
                  >
                    <MessageSquare size={14} /> Open Chat & Google Meet <ArrowRight size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STANDING SUBJECT ROOMS ── */}
      {sessions.some(s => s.isDefaultRoom) && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)', margin: '0 0 2px' }}>
                Standing Subject Rooms (Google Meet)
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                Open 24/7 for collaborative study and drop-in doubt solving via Google Meet.
              </p>
            </div>
            <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#2563eb', background: 'rgba(37,99,235,0.08)', padding: '3px 8px', borderRadius: 12 }}>
              Grade 8-12 + UG
            </span>
          </div>
          <div className="subject-room-grid">
            {sessions.filter(s => s.isDefaultRoom).map(s => {
              const sId = s.id || s._id;
              const isEnrolled = s.isEnrolled || joinedSessionMap.has(sId);
              const enrolledLink = joinedSessionMap.get(sId);
              return (
                <PeerSessionCard
                  key={sId}
                  session={s}
                  user={user}
                  isEnrolled={isEnrolled}
                  enrolledLink={enrolledLink}
                  onJoinRequest={handleJoinRequest}
                  onStart={handleStartSession}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── PEER-HOSTED LIVE SESSIONS ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)', margin: '0 0 2px' }}>
              Peer-Hosted Study Sessions
            </h3>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
              Student-led study sessions on focused topics.
            </p>
          </div>
        </div>

        {sessionsLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {[1, 2, 3].map(i => <div key={i} className="glass-card animate-pulse-slow" style={{ height: '14rem' }} />)}
          </div>
        ) : sessions.filter(s => !s.isDefaultRoom).length === 0 ? (
          <div className="glass-card" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
            <Video size={36} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
            <p style={{ fontStyle: 'italic' }}>No peer-hosted rooms open right now — click "Host a Study Room" to start one!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
            {sessions.filter(s => !s.isDefaultRoom).map(s => {
              const sId = s.id || s._id;
              const isEnrolled = s.isEnrolled || joinedSessionMap.has(sId);
              const enrolledLink = joinedSessionMap.get(sId);
              return (
                <PeerSessionCard
                  key={sId}
                  session={s}
                  user={user}
                  isEnrolled={isEnrolled}
                  enrolledLink={enrolledLink}
                  onJoinRequest={handleJoinRequest}
                  onStart={handleStartSession}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── STUDY GROUPS & COMMUNITIES ── */}
      <div className="community-layout">
        <div className="community-main-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)', margin: '0 0 2px' }}>
                Subject Communities & Study Groups
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                Join active channels to chat, ask doubts, and hop on group video calls.
              </p>
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#b45309' }}>
              {filtered.length} Channels
            </span>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[1, 2, 3].map(i => <div key={i} className="glass-card animate-pulse-slow" style={{ height: '5rem' }} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontStyle: 'italic', fontSize: '0.9rem' }}>No communities found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {filtered.map(c => {
                const cId = c.id || c._id;
                const isMember = c.isMember || joinedCommunityIds.has(cId);
                return (
                  <CommunityCard
                    key={cId}
                    c={c}
                    isMember={isMember}
                    onJoin={handleJoinGroup}
                    onOpenDiscussion={() => setActiveDiscussionCommunity(c)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="community-sidebar">
          <div style={{ position: 'relative' }}>
            <Search size={16} color="rgba(0,0,0,0.3)" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Find groups (e.g. AI, Physics, DSA)..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ background: '#ffffff', border: '1px solid var(--slate-200)', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.625rem 1rem 0.625rem 2.25rem', fontSize: '0.85rem', outline: 'none', width: '100%', fontFamily: 'inherit', marginBottom: '1.25rem' }}
            />
          </div>
          
          <div className="glass-card" style={{ position: 'relative', overflow: 'hidden', padding: '1.25rem' }}>
            <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.35rem', color: 'var(--navy-dark)' }}>Create a Study Circle</h3>
            <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '1.25rem', lineHeight: 1.5 }}>Start a new peer study group for your college or cohort.</p>
            <button
              onClick={() => showAlert('Coming Soon', 'Custom student group creation with role permissions will launch in the next sprint!')}
              style={{ width: '100%', padding: '0.7rem', background: 'linear-gradient(135deg,#f4c430,#d4a017)', color: 'black', borderRadius: '0.75rem', fontWeight: 900, fontSize: '0.78rem', cursor: 'pointer', border: 'none', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(244,196,48,0.2)' }}
            >
              + Propose Group
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {confirmingSession && (
        <JoinPeerRoomConfirmationModal
          session={confirmingSession}
          loading={joiningLoading}
          onConfirm={handleConfirmJoin}
          onClose={() => setConfirmingSession(null)}
        />
      )}

      {activeDiscussionCommunity && (
        <GroupDiscussionModal
          community={activeDiscussionCommunity}
          user={user}
          onClose={() => setActiveDiscussionCommunity(null)}
        />
      )}

      {showHostModal && <HostSessionModal onClose={() => setShowHostModal(false)} onCreated={loadSessions} />}
    </div>
  );
}
